import { ResponseMessages, MessageStatusEnum, MetaResponseMessages } from "@sms/shared/message/message.interface";
import React, { Dispatch, RefObject, memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import usePrevious from "../../../../../app/helpers/hooks";
import { selectCurrentMessages } from "../../../../../app/selectors/messages";
import {
  getMessagesNextPageAction,
  updateMessageStatusToReadAction
} from "../../../../../app/messages/messages.actions";

import MessageItem from "./message-item/message-item.component";
import styles from "./message-list.module.scss";
import MessageServiceItem from "./message-services-item/message-services-item.component";

import { ApplicationStore } from "~/app/application.store";

const MessageListComponent: React.FC = memo(() => {
  const dispatch: Dispatch<any> = useDispatch();

  const [isLoaded, setIsLoaded]: [boolean, React.Dispatch<React.SetStateAction<boolean>>] = useState<boolean>(false);

  const ref: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const refHasNextPage: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const refList: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const messages: ResponseMessages[] = useSelector(selectCurrentMessages());
  const metaMessages: MetaResponseMessages | null = useSelector((store: ApplicationStore) => store.message.meta);

  const isLoadingMessages: boolean = useSelector((store: ApplicationStore) => store.message.isLoadingMessages);

  const getMessagesNextPage: () => void = useCallback(() => {
    dispatch(getMessagesNextPageAction());
  }, [dispatch]);

  const handleMessageStatusReaded: (message: ResponseMessages) => void = useCallback(
    (message: ResponseMessages): void => {
      if (message.status === MessageStatusEnum.UNREAD) {
        dispatch(updateMessageStatusToReadAction(message.id));
      }
    },
    [dispatch]
  );

  useLayoutEffect(() => {
    if (messages.length !== 0 && !isLoaded) {
      setIsLoaded(true);
      scrollToBottom();
    }

    if (messages.length !== 0 && prevMessagesState?.at(-1)?.id !== messages.at(-1)?.id) {
      scrollToBottom();
    }
  }, [messages]);

  const prevMessagesState: ResponseMessages[] | null = usePrevious<ResponseMessages[] | null>(messages);

  // useEffect(() => {
  //   if (refHasNextPage.current) {
  //     const observer: IntersectionObserver = new IntersectionObserver(
  //       entries => {
  //         entries.forEach(entry => {
  //           if (entry.isIntersecting && isLoaded) {
  //             getMessagesNextPage();
  //           }
  //         });
  //       },
  //       { root: refList.current?.parentElement ?? null, rootMargin: "100px 0px 0px 0px" }
  //     );

  //     observer.observe(refHasNextPage.current);
  //     return (): void => {
  //       if (refHasNextPage.current) {
  //         observer.unobserve(refHasNextPage.current);
  //       }
  //     };
  //   }
  // }, [metaMessages?.hasNextPage, refHasNextPage.current]);

  useEffect(() => {
    const handleScroll: () => void = () => {
      if (
        refList.current?.parentElement?.scrollTop &&
        refList.current?.parentElement?.scrollTop < 200 &&
        isLoaded &&
        metaMessages?.hasNextPage &&
        !isLoadingMessages
      ) {
        getMessagesNextPage();
      }
    };

    refList.current?.parentElement?.addEventListener("scroll", handleScroll);

    return () => {
      refList.current?.parentElement?.removeEventListener("scroll", handleScroll);
    };
  }, [metaMessages?.hasNextPage, refHasNextPage.current, isLoadingMessages]);

  const scrollToBottom: () => void = useCallback((): void => {
    if (ref.current) {
      ref.current.scrollIntoView();
    }
  }, []);

  return (
    <div className={styles.messageList} ref={refList}>
      {metaMessages?.hasNextPage && <div ref={refHasNextPage}></div>}
      {messages?.map(message => (
        <div key={message.id}>
          {message.chat?.type === "default" && (
            <MessageItem message={message} handleMessageStatusReadUpdate={handleMessageStatusReaded} />
          )}
          {message.chat?.type !== "default" && (
            <MessageServiceItem message={message} handleMessageStatusReadUpdate={handleMessageStatusReaded} />
          )}
        </div>
      ))}
      <div style={{ float: "left", clear: "both" }} ref={ref}></div>
    </div>
  );
});

MessageListComponent.displayName = "MessageListComponent";

export default MessageListComponent;
